/**
 * Calendar dates as ISO `YYYY-MM-DD` strings — the only date form allowed in
 * Redux state, RTK Query args and data-source contracts (serializable, stable
 * cache keys, timezone-free).
 *
 * Conversions are in *local* time, matching the prototype, whose engine and
 * date pickers work on local-midnight timestamps.
 */
export type IsoDate = string;

const pad = (n: number) => String(n).padStart(2, '0');

export function toIsoDate(value: Date | number): IsoDate {
  const d = typeof value === 'number' ? new Date(value) : value;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local midnight of an ISO date, as a `Date`. */
export function fromIsoDate(iso: IsoDate): Date {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

/** Local midnight of an ISO date, as epoch milliseconds. */
export function isoToMs(iso: IsoDate): number {
  return fromIsoDate(iso).getTime();
}

export function isIsoDate(value: unknown): value is IsoDate {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

export type IsoRange = { from: IsoDate; to: IsoDate };
